/**
 * PinDots — визуальный индикатор PIN-кода (4 кружка).
 *
 * Banking-pattern (Apple Lock / Avast / Тинькофф): фиксированное число
 * dots, заполняются по мере ввода. Не редактируемое поле — ввод через
 * <NumPad> или клавиатуру выше.
 *
 * @example
 *   <PinDots length={4} filled={pin.length} error={hasError} />
 */
import { cn } from '@/lib/utils'

export interface PinDotsProps {
  /** Сколько ячеек всего (default 4) */
  length?: number
  /** Сколько уже заполнено цифрами */
  filled: number
  /** error-state — красный + shake-анимация */
  error?: boolean
  /** success-state — зелёный flash на 300ms перед redirect/transition */
  success?: boolean
  /** ARIA-метка для screen-readers */
  label?: string
  className?: string
}

export function PinDots({
  length = 4,
  filled,
  error = false,
  success = false,
  label = 'PIN-код',
  className,
}: PinDotsProps) {
  return (
    <div
      role="status"
      aria-label={`${label}: ${filled} из ${length} введено`}
      data-testid="pin-dots"
      className={cn(
        'flex gap-3.5 justify-center items-center',
        error && 'animate-shake',
        className,
      )}
    >
      {Array.from({ length }).map((_, i) => (
        <span
          key={i}
          data-filled={i < filled}
          data-error={error}
          data-success={success}
          className={cn(
            // Фиксированный box 16×16 (включая border-2 — Tailwind использует
            // box-sizing: border-box по умолчанию), чтобы при заполнении не было
            // layout shift и numpad ниже не прыгал.
            'inline-block w-4 h-4 rounded-full border-2 transition-colors duration-150',
            i < filled
              ? 'bg-text-primary border-text-primary'
              : 'bg-transparent border-text-muted',
            // Success-state перебивает filled — зелёная вспышка перед redirect.
            // Используется в PinLogin/PinSetup для positive-feedback.
            success && 'bg-status-success-text border-status-success-text',
            // Error-state — самый высокий приоритет.
            error && 'bg-status-error-text border-status-error-text',
          )}
        />
      ))}
    </div>
  )
}
